import { pathByRouteName } from './routes';
import routeConfiguration from '../routing/routeConfiguration';
import { hasCompletedAllProfileRequirements } from './providerRequirementsCheck';

/** Content id for HelpFlyout checklist (must match helpContentRegistry). */
export const PROVIDER_REQUIREMENTS_HELP_CONTENT_ID = 'provider-requirements-help';

export const HELP_QUERY_PARAM = 'help';

/**
 * Check if a provider-main user has completed all requirements (excluding listings)
 * @param {Object} currentUser - The current user object
 * @returns {boolean} True if all requirements are completed
 */
export const hasCompletedAllRequirements = (currentUser, options = {}) =>
  hasCompletedAllProfileRequirements(currentUser, options);

/**
 * Whether incomplete providers should auto-open requirements help after default redirect.
 * Uses the synced Sharetribe flag (same as search eligibility).
 *
 * @param {Object} currentUser
 * @returns {boolean}
 */
export const shouldOpenProviderRequirementsHelp = currentUser => {
  const publicData = currentUser?.attributes?.profile?.publicData || {};
  return (
    publicData.userType === 'provider-main' && publicData.providerRequirementsComplete !== true
  );
};

/**
 * Append ?help=<id> (or &help=) to a path for one-shot HelpFlyoutUrlSync.
 *
 * @param {string} path
 * @param {string} helpContentId
 * @returns {string}
 */
export const appendHelpQueryToPath = (path, helpContentId) => {
  if (!path || !helpContentId) {
    return path;
  }
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}${HELP_QUERY_PARAM}=${encodeURIComponent(helpContentId)}`;
};

/**
 * Get the default page route name and parameters for a given user type.
 * Providers always land on Inbox sales (requirements help opens via query param, not a page redirect).
 *
 * @param {string} userType - The user type (customer-main, provider-main, super)
 * @param {Object} currentUser - The current user object (optional; unused for destination)
 * @returns {Object} Object containing route name and optional params
 */
export const getUserTypeDefaultPage = (userType, currentUser = null) => {
  switch (userType) {
    case 'customer-main':
      return { name: 'SearchPage' };
    case 'provider-main':
      return { name: 'InboxPage', params: { tab: 'sales' } };
    case 'super':
      return { name: 'AdminPage' };
    default:
      return { name: 'ExternalLandingPage', url: 'https://findyourkind.com/' };
  }
};

/**
 * Build default redirect path, appending help= when the provider flag is incomplete.
 *
 * @param {string} userType
 * @param {Object} currentUser
 * @param {Array} routes - Route configuration (defaults to app routes)
 * @returns {string}
 */
export const getUserTypeDefaultRedirectPath = (
  userType,
  currentUser = null,
  routes = routeConfiguration
) => {
  const page = getUserTypeDefaultPage(userType, currentUser);
  if (page.name === 'ExternalLandingPage') {
    return page.url;
  }
  let path = pathByRouteName(page.name, routes, page.params || {});
  if (shouldOpenProviderRequirementsHelp(currentUser)) {
    path = appendHelpQueryToPath(path, PROVIDER_REQUIREMENTS_HELP_CONTENT_ID);
  }
  return path;
};

/**
 * Search string for NamedRedirect when help should open (includes leading ?).
 *
 * @param {Object} currentUser
 * @returns {string|undefined}
 */
export const getProviderRequirementsHelpSearch = currentUser => {
  if (!shouldOpenProviderRequirementsHelp(currentUser)) {
    return undefined;
  }
  return `?${HELP_QUERY_PARAM}=${encodeURIComponent(PROVIDER_REQUIREMENTS_HELP_CONTENT_ID)}`;
};

/**
 * Get the default page path for a given user type
 * @param {string} userType - The user type (customer-main, provider-main, super)
 * @returns {string} The path for the default page
 */
export const getUserTypeDefaultPath = userType => {
  const routeInfo = getUserTypeDefaultPage(userType);

  // Special handling for provider-main to redirect to sales tab
  if (userType === 'provider-main') {
    return pathByRouteName('InboxPage', routeConfiguration) + '/sales';
  }

  // Handle external landing page
  if (routeInfo.name === 'ExternalLandingPage') {
    return routeInfo.url;
  }

  return pathByRouteName(routeInfo.name, routeConfiguration);
};
